import { memo, useEffect, useRef, useState } from "react";

import { LoadingOutlined, ToolFilled, SettingOutlined, InfoCircleOutlined, HourglassFilled, CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { Row, Image, Card, Modal, Button, notification, Space } from "antd";

import { ResponsiveLine } from '@nivo/line';
import { BasicTooltip } from '@nivo/tooltip';

import ValidationImage from "./ValidationImage";

import { deepEquals, friendlyName, paramClass } from "../util";
import { useSettings } from "./TrainingOverview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/sharp-light-svg-icons";
import { fetchRunArgs, getArgumentChanges, putRunArgs } from "../api";

import "../style/args-history.scss";
import { RunConfig } from "./TrainingSettingsModal";

const { PreviewGroup } = Image;



const TrainingRun = memo(function TrainingRun(props) {
    const { data, modal, status, derive } = props;
    const { args, images, changes = [], run_id } = data;
    const argumentChanges = useRefreshing(() => getArgumentChanges(run_id));

    // if (data && status && false) {
    //     console.log(JSON.stringify(data));
    //     console.log(JSON.stringify(status));
    // }

    const [collapsed, setCollapsed] = useState(false);

    const changesAtSteps = argumentChanges?.map(c => c.modify_args?.step ?? -1) ?? [];
    const argChanges = changesAtSteps.map(step => images.find(img => img.step < step)?.step).filter(v => v);

    return (
        <Card className="run-container" title={<RunCardTitle {...{ status, args, images, changes, setCollapsed, derive }} />}>
            <PreviewGroup preview={{ toolbarRender: () => <></>, countRender: () => <></> }}>
                {!collapsed && (
                    <Row gutter={[0, 0]}>
                        {images.map(r => <ValidationImage key={r.run_id + r.step} data={r} modal={modal} changed={argChanges.includes(r.step)} />)}
                    </Row>
                )}
            </PreviewGroup>
        </Card>
    );
}, deepEquals);

const RunCardTitle = (props) => {
    const { images, args, status, setCollapsed, changes, derive } = props;
    const { pinnedParams } = useSettings()[0];
    const [showArgs, setShowArgs] = useState(false);
    const [abortingRun, setAbortingRun] = useState(false);
    const [currentArgs, setCurrentArgs] = useState(null);
    const run_id = status ? status.run_id : (images && images.length ? images[0].run_id : null);

    const [paramsModal, contextHolderModal] = Modal.useModal();

    const statusRef = useRef(status);
    statusRef.current = status;

    const changesRef = useRef(changes);
    changesRef.current = changes;

    const openModal = () => paramsModal.info({
        title: "Experiment: " + run_id,
        width: 800,
        maskClosable: true,
        transitionName: null,
        icon: null,
        style: { maxHeight: '80vh' },
        content: <RunConfig runId={run_id} status={status} statusRef={statusRef} paramChanges={changesRef} />, // </RunConfig><ArgsEditor runId={run_id} />
    });

    const titleStr = args.output_dir.split('/').slice(-2).join('/');

    const [notif, contextHolder] = notification.useNotification();

    useEffect(() => {
        let k;

        const update = () => {
            k = window.setTimeout(update, 5000);

            if (statusRef.current) {
                fetchRunArgs(run_id).then(d => setCurrentArgs(d));
            }
        }

        k = window.setTimeout(update, 5000);

        return () => window.clearTimeout(k);
    }, [run_id]);

    const setAbort = async (abort) => {
        if (abortingRun) {
            return;
        }

        setAbortingRun(true);

        try {
            const runArgs = await fetchRunArgs(run_id);
            runArgs.abort = abort;
            await putRunArgs(run_id, runArgs);
            setCurrentArgs(runArgs);
        } catch (e) {
            console.error(e);
        }

        setAbortingRun(false);
    };

    const aborted = !!currentArgs?.abort;
    const abortAck = !!status?.current_status?.args?.abort;

    const deriveRun = () => derive({ ...args });

    const sortedChanges = [...changes].sort((a, b) => a.param.localeCompare(b.param));

    return (<>
        {contextHolder}
        {contextHolderModal}

        <span className="btn-edit-args" onClick={openModal}>
            <SettingOutlined />
        </span>

        <span className="btn-edit-args" onClick={deriveRun} style={{ marginLeft: 4 }}>
            <CopyOutlined />
        </span>

        <span className="title-text" onClick={() => setCollapsed(v => !v)}>
            {titleStr} {args.nsfw && <FontAwesomeIcon icon={faWarning} />} {status && <> <LoadingOutlined /> {status.current_status.step} {(status.current_status.vmem_kb_rsvd / 1024 / 1024).toFixed(1)} GB</>}
            <span className="num-changes" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowArgs(v => !v) }}>
                <ToolFilled /> {changes.length}
            </span>

            <span className="pinned-params">
                {pinnedParams.map(pp => (
                    <span key={pp} className={"pinned-param " + paramClass(changes, pp)}>
                        {friendlyName(pp)}: {(args[pp] ?? '').toString()}
                    </span>
                ))}
            </span>
        </span>

        {status ?
            <Button danger icon={(aborted && !abortAck) ? <HourglassFilled spin /> : <DeleteOutlined />} loading={abortingRun} size="small" type={aborted ? "default" : "text"} onClick={() => setAbort(!aborted)} />
            :
            <span className='step-status'>Finished</span>
        }

        {showArgs ? <div className="title-params-wrapper">
            <div className="title-params">
                {sortedChanges.map(c => (
                    <div key={c.param} className="change-item">
                        <InfoCircleOutlined /> <b>{c.param}</b> set to <pre title={c.to}>{JSON.stringify(c.to)}</pre> (was <pre title={c.from} className="muted">{JSON.stringify(c.from)}</pre>)
                    </div>
                ))}
            </div>
        </div> : null}
    </>);
}


const Tooltip = memo(function Tooltip({ slice: { id, points, x, x0 }, axis }) {
    const singular = points.reduce((acc, v) => v.serieId in acc ? acc : Object.assign(acc, { [v.serieId]: v }), {});

    return <>
        {
            Object.values(singular).map(point => <BasicTooltip
                key={point.serieId}
                enableChip
                id={`${point.data.x} ${point.serieId}`}
                color={point.serieColor}
                value={point.data.yFormatted}
            />)
        }</>
}, deepEquals);

const _TrainingChart = ({ data, markers, lossClip }) => {
    /** @type {CartesianMarkerProps[]} */
    const changeMarkers = markers.map(marker => ({
        axis: "x",
        legend: marker.modified_keys.join(', '),
        legendOrientation: "vertical",
        lineStyle: { stroke: "yellow", strokeWidth: 1 },
        textStyle: { fill: "white", fontSize: 10 },
        value: marker.step,
    }));


    const lossClipValues = [[0, lossClip]];
    for (const change of markers) {
        if (change.modified_keys.includes("loss_clip")) {
            lossClipValues.push([change.step, change.new.loss_clip]);
        }
    }

    const verticalMarkers = lossClipValues.map(([step, clip]) => ({
        axis: "y",
        legend: `${clip.toFixed(4)}`,
        legendOrientation: "horizontal",
        // legendOffsetY: 0,
        lineStyle: { stroke: "red", strokeWidth: 1 },
        textStyle: { fill: "red", fontSize: 10 },
        value: clip,
    }));

    return (
        <ResponsiveLine
            data={data}
            margin={{ top: 10, right: 50, bottom: 30, left: 10 }}
            xScale={{ type: 'linear', min: "auto" }}
            yScale={{ type: 'linear' }}
            yFormat=" >-.6f"
            enableSlices="x"
            curve="linear"
            axisTop={null}
            axisLeft={null}
            animate={false}
            axisRight={{ format: '.6f', legendOffset: 0 }}
            enableGridX={true}
            colors={{ scheme: 'tableau10' }}
            theme={trainingChartTheme}
            lineWidth={1}
            enablePoints={false}
            crosshairType="cross"
            enableTouchCrosshair={true}
            useMesh={false}
            legends={[]}
            sliceTooltip={Tooltip}
            markers={[...changeMarkers, ...verticalMarkers]}
        />
    );
}
export const TrainingChart = _TrainingChart;

const trainingChartTheme = {
    "tooltip": {
        "container": {
            "color": "black"
        }
    },
    "grid": {
        "line": {
            "stroke": "#777777"
        }
    },
    "axis": {
        "domain": {
            "line": {
                "stroke": "#777777",
                "strokeWidth": 1
            }
        },
        // "grid": 6,
        "legend": {
            "text": {
                "fontSize": 12,
                "fill": "#ff0000",
                "outlineWidth": 0,
                "outlineColor": "transparent"
            }
        },
        "ticks": {
            "text": {
                "fontSize": 11,
                "fill": "#c0c0c0",
                "outlineWidth": 0,
                "outlineColor": "transparent"
            }
        }
    },
};

function useRefreshing(fetcher, intervalMillis = 10000, initialValue = null) {
    const [value, setValue] = useState(initialValue ?? null);

    useEffect(() => {
        const asyncFn = (async () => {
            const value = await fetcher();
            setValue(value);
        });

        const key = window.setInterval(() => {
            asyncFn();
        }, intervalMillis);

        asyncFn();
        return () => window.clearInterval(key);
    }, [intervalMillis]);

    return value;
}

export default TrainingRun;